import {useCallback, useEffect, useRef} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {usePropertyHighlightsAnalyticsPayload} from 'components/PropertyHighlights'
import {useOfferTrackingContextWithFallback} from 'context/offerTracking'
import {OfferViewType, useOfferViewContext} from 'context/offerViewContext'
import {useOfferCheckInCheckoutDates} from 'hooks/useOfferCheckInCheckoutDates'
import {
  getOfferContext,
  getParametrizedHotelContext
} from 'middleware/analytics/selectors'
import {
  getIsAnchorHotelViewed,
  getIsOfferViewed
} from 'modules/analytics/selectors'
import {setAnchorHotelViewed, setOfferViewed} from 'modules/analytics/slice'
import {
  getAnonymousId,
  getCurrencyCode,
  getLanguageCode,
  getLocalTaxesIncluded,
  getTaxesIncluded,
  getUserCountryCode
} from 'modules/meta/selectors'
import {
  getAppLockedDealConfig,
  getHotel,
  getHotelCity,
  getHotelOfferEntity,
  getIsAnchorHotel,
  getOfferAccessFlowSettings,
  getSearchParameters
} from 'modules/sapiSearch/selectors'
import {SearchOffer} from 'modules/sapiSearch/slice'
import {
  getCheapestOfferTotalRate,
  getCheapestTopOfferByNightlyRate
} from 'utils/offers'

import {SIZES} from '@vio/atlas/Image'
import {imageProvider} from '@vio/atlas/testutils/imageProvider'
import {persistAppLockedDealImpressionsCount} from '@vio/core/src/_web/appLockedDeal/impressionsCount'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/components/TeamContext'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {customerIo} from '@vio/core/src/analytics/services/CustomerIo'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity,
  Page
} from '@vio/core/src/analytics/types/Events'
import {getShouldSeeOffersUnlocked} from '@vio/core/src/auth/modules/selectors'
import {
  getUserId,
  getWhiteLabelUserId,
  selectIsLoading
} from '@vio/core/src/auth/modules/selectors'
import isBofhOffer from '@vio/core/src/offer/business/isBofhOffer'
import {getEpochSeconds} from '@vio/core/src/utils/date'

type InViewRef = (node?: Element) => void

const VIEW_THRESHOLD = 0.5
const VISIBILITY_CHECK_INTERVAL = 1000

/**
 * Determines whether the specified element is visible (not covered by another element).
 * WARNING: This function is not performant, so use it sparingly.
 */
const isElementVisible = (element?: Element): boolean => {
  if (!element) return false

  const {top, left, height, width} = element.getBoundingClientRect()
  const middlePoint = [left + width / 2, top + height / 2] as const

  const elementAtPoint = document.elementFromPoint(...middlePoint)

  return element.contains(elementAtPoint)
}

export const useTrackOfferView = (
  offer: SearchOffer,
  hotelId: string,
  component: string,
  bannerId?: string
): InViewRef => {
  const dispatch = useDispatch()
  const dispatchTeamEvent = useDispatchTeamEvent()
  // Added as part of dcbc5616-property-highlights-v1
  const offerView = useOfferViewContext()
  const propertyHighlightsPayload = usePropertyHighlightsAnalyticsPayload(
    offerView === OfferViewType.SRP ? hotelId : undefined
  )
  const offerRef = useRef<Element>(null)
  const offerIsViewed = useSelector(state => getIsOfferViewed(state, offer?.id))

  const {ref: inViewRef, inView} = useInView({
    skip: offerIsViewed,
    threshold: VIEW_THRESHOLD
  })

  const setCombinedRefs = useCallback(
    (node: Element): void => {
      offerRef.current = node
      inViewRef(node)
    },
    [inViewRef]
  )

  const hotel = useSelector(state => getHotel(state, hotelId))
  const hotelCity = useSelector(state => getHotelCity(state, hotelId))

  // Allow context to overwrite default selectors
  const {offerEntity, offerContext: originalOfferContext} =
    useOfferTrackingContextWithFallback({
      offer,
      offerEntity: useSelector(state => getHotelOfferEntity(state, hotelId)),
      offerContext: useSelector(state =>
        getOfferContext(state, hotelId, offer?.id, component)
      )
    })

  const cheapestOffer = getCheapestTopOfferByNightlyRate(offerEntity?.offers)
  const cheapestTotalRate = getCheapestOfferTotalRate(offerEntity?.offers)

  const {offerCheckIn, offerCheckOut} = useOfferCheckInCheckoutDates({
    sourceComponent: component
  })

  const offerContext = {
    ...originalOfferContext,
    checkIn: offerCheckIn,
    checkOut: offerCheckOut
  }

  const isAnchorHotel = useSelector(state => getIsAnchorHotel(state, hotelId))

  const anchorHotelIsViewed = useSelector(state =>
    getIsAnchorHotelViewed(state, hotelId)
  )

  const shouldSeeOffersUnlocked = useSelector(getShouldSeeOffersUnlocked)
  const isLoading = useSelector(selectIsLoading)

  const analyticsContext = {
    [AnalyticsContext.HotelContext]: getParametrizedHotelContext(hotelId),
    [AnalyticsContext.OfferContext]: offerContext
  }

  const {isCheapest} = offerContext
  const anonymousId = useSelector(getAnonymousId)
  const {brand} = useBrand()
  const cio = customerIo(brand)
  const userId = useSelector(getUserId)
  const whiteLabelUserId = useSelector(getWhiteLabelUserId)
  const userCountryCode = useSelector(getUserCountryCode)
  const sapiSearchParameters = useSelector(getSearchParameters)
  const localTaxesIncluded = useSelector(getLocalTaxesIncluded)
  const taxesIncluded = useSelector(getTaxesIncluded)
  const currencyCode = useSelector(getCurrencyCode)
  const languageCode = useSelector(getLanguageCode)

  const {appDownloadLinkId} = useSelector(state =>
    getAppLockedDealConfig(state, hotelId, offer?.id)
  )
  const {isAuthLocked, isAppLocked} = useSelector(state =>
    getOfferAccessFlowSettings(state, hotelId, offer?.id)
  )

  const isAnchorAppLockedDeal = isAnchorHotel && isAppLocked

  const impressionDayCount = persistAppLockedDealImpressionsCount(
    isAnchorAppLockedDeal
  )

  useEffect(() => {
    /* If auth is still loading, isOfferLocked will be true
     * even if the user is logged in.
     * We need to wait to fire the event until auth is done
     * loading
     */
    if (isLoading) {
      return
    }

    if (inView && !offerIsViewed) {
      const intervalId = setInterval(() => {
        const offerIsVisible = isElementVisible(offerRef.current)

        if (offerIsVisible) {
          if (isAppLocked) {
            dispatchTeamEvent(
              trackEvent({
                category: Category.System,
                entity: Entity.AppExclusiveOffer,
                action: Action.Displayed,
                analyticsContext: {
                  [AnalyticsContext.HotelContext]:
                    getParametrizedHotelContext(hotelId),
                  [AnalyticsContext.OfferContext]: offerContext
                },
                payload: {
                  bannerId: bannerId || appDownloadLinkId,
                  impressionDayCount
                }
              })
            )
          }

          dispatchTeamEvent(
            trackEvent({
              category: Category.User,
              entity: Entity.Offer,
              action: Action.Viewed,
              component,
              analyticsContext,
              payload: {
                isOfferLocked: isAuthLocked,
                isLogoVisible: isBofhOffer(offer),
                isPriceVisible: true,
                // Temporary params to debug issue on mobile
                shouldSeeOffersUnlocked: shouldSeeOffersUnlocked,
                // Added as part of dcbc5616-property-highlights-v1
                ...propertyHighlightsPayload
              }
            })
          )

          if (isAnchorHotel && isCheapest) {
            const {imageURIs, hotelName, starRating, displayAddress} =
              hotel || {}
            const hotelImageUrl = imageURIs?.[0]
              ? imageProvider(imageURIs[0], SIZES['large'])
              : undefined
            const {checkIn, checkOut, rooms} = sapiSearchParameters || {}

            const {roomName} = offer || {}

            cio.trackEvent({
              name: `${Category.User}_${Entity.CheapestAnchorOffer}_${Action.Viewed}`,
              pageName: Page.Search,
              userId: userId || whiteLabelUserId,
              anonymousId,
              starRating,
              hotelAddress: displayAddress,
              hotelName,
              hotelCity,
              hotelImageUrl,
              isAnchorHotel,
              hotelId,
              checkIn: getEpochSeconds(checkIn),
              checkOut: getEpochSeconds(checkOut),
              rooms,
              roomName,
              currency: currencyCode,
              locale: languageCode,
              country: userCountryCode,
              rate: {
                base: cheapestOffer?.rate.base,
                hotelFees: cheapestOffer?.rate.hotelFees,
                taxes: cheapestOffer?.rate.taxes,
                taxesIncluded: Boolean(taxesIncluded),
                localTaxesIncluded: Boolean(localTaxesIncluded),
                totalRate: cheapestTotalRate
              }
            })
          }

          dispatch(setOfferViewed({offerId: offer.id}))

          if (isAnchorHotel && !anchorHotelIsViewed) {
            dispatch(
              setAnchorHotelViewed({
                hotelId,
                chainID: hotel?.chainID,
                parentChainID: hotel?.parentChainID
              })
            )
          }

          clearInterval(intervalId)
        }
      }, VISIBILITY_CHECK_INTERVAL)

      return () => {
        clearInterval(intervalId)
      }
    }
  }, [
    inView,
    isLoading,
    anchorHotelIsViewed,
    isAnchorHotel,
    offerIsViewed,
    propertyHighlightsPayload
  ])

  return setCombinedRefs
}
